import { useEffect, useRef } from "react";

import { BillboardHud } from "../components/BillboardHud";
import { BillboardModal } from "../components/BillboardModal";
import { EntryPanel } from "../components/EntryPanel";
import { GarageModal } from "../components/GarageModal";
import { InfoModal } from "../components/InfoModal";
import { MockTrafficPanel } from "../components/MockTrafficPanel";
import { StaticTrafficBoard } from "../components/StaticTrafficBoard";
import { TrafficHud } from "../components/TrafficHud";
import { TripReport } from "../components/TripReport";
import { saveMockDriver } from "../features/driver/mockIdentity";
import { billboardDisplaySnapshots } from "../features/billboards/billboardDisplay";
import { referralCodeFromLocation } from "../features/sharing/shareRoute";
import { TrafficCanvas } from "../renderer/TrafficCanvas";
import { analytics } from "../services/analytics/analytics";
import { useBillboardStore } from "../stores/useBillboardStore";
import { useTrafficStore } from "../stores/useTrafficStore";

export function App() {
  const phase = useTrafficStore((state) => state.phase);
  const canvasFailed = useTrafficStore((state) => state.canvasFailed);
  const reducedMotion = useTrafficStore((state) => state.reducedMotion);
  const performanceTier = useTrafficStore((state) => state.performanceTier);
  const transportMode = useTrafficStore((state) => state.transportMode);
  const laneChangeSequence = useTrafficStore(
    (state) => state.laneChangeSequence,
  );
  const changeLane = useTrafficStore((state) => state.changeLane);
  const setPerformanceTier = useTrafficStore(
    (state) => state.setPerformanceTier,
  );
  const initializeSessionRuntime = useTrafficStore(
    (state) => state.initializeSessionRuntime,
  );
  const shutdownSessionRuntime = useTrafficStore(
    (state) => state.shutdownSessionRuntime,
  );
  const setDocumentVisible = useTrafficStore(
    (state) => state.setDocumentVisible,
  );
  const tick = useTrafficStore((state) => state.tick);
  const billboardSlots = useBillboardStore((state) => state.slots);
  const billboardsEnabled = useBillboardStore((state) => state.enabled);
  const initializeBillboards = useBillboardStore((state) => state.initialize);
  const shutdownBillboards = useBillboardStore((state) => state.shutdown);
  const openBillboard = useBillboardStore((state) => state.open);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    initializeSessionRuntime();
    initializeBillboards();
    analytics.track("experiment_opened", {
      sourceCategory: referralCodeFromLocation(
        location.pathname,
        location.search,
      )
        ? "referral"
        : "direct",
      deviceClass:
        innerWidth < 600 ? "compact" : innerWidth < 1024 ? "medium" : "wide",
    });
    analytics.track("entry_visible", {
      mode: transportMode,
      trafficState: useTrafficStore.getState().trafficState,
    });

    let previous = performance.now();
    const interval = window.setInterval(() => {
      const now = performance.now();
      tick((now - previous) / 1000);
      previous = now;
    }, 250);
    const handleVisibility = () => {
      previous = performance.now();
      setDocumentVisible(!document.hidden);
    };
    const persistLocalTrip = (event: PageTransitionEvent) => {
      const state = useTrafficStore.getState();
      if (
        state.profile &&
        state.distanceMiles > 0 &&
        (state.phase === "driving" || state.phase === "exiting")
      ) {
        saveMockDriver({
          ...state.profile,
          odometerMiles: state.profile.odometerMiles + state.distanceMiles,
        });
      }
      if (
        event.persisted ||
        state.transportMode !== "supabase" ||
        !state.isPrimaryTab ||
        !state.sessionId
      ) {
        return;
      }
      navigator.sendBeacon(
        "/api/session/leave",
        new Blob([JSON.stringify({ sessionId: state.sessionId })], {
          type: "application/json",
        }),
      );
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", persistLocalTrip);
    return () => {
      window.clearInterval(interval);
      shutdownSessionRuntime();
      shutdownBillboards();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", persistLocalTrip);
    };
  }, [
    initializeSessionRuntime,
    initializeBillboards,
    setDocumentVisible,
    shutdownSessionRuntime,
    shutdownBillboards,
    tick,
    transportMode,
  ]);

  useEffect(() => {
    if (performanceTier === "reduced") return;
    let frameRequest = 0;
    let previous = performance.now();
    let elapsed = 0;
    let measured = 0;
    let frames = 0;
    const sample = (now: number) => {
      const delta = now - previous;
      previous = now;
      if (!document.hidden && delta < 250) {
        elapsed += delta;
        measured += delta;
        frames += 1;
      }
      if (elapsed < 1800) {
        frameRequest = requestAnimationFrame(sample);
        return;
      }
      const averageFrameMilliseconds = measured / Math.max(1, frames);
      if (averageFrameMilliseconds > 34) {
        setPerformanceTier("reduced");
      } else if (performanceTier === "high" && averageFrameMilliseconds > 22) {
        setPerformanceTier("standard");
      }
    };
    frameRequest = requestAnimationFrame(sample);
    return () => cancelAnimationFrame(frameRequest);
  }, [performanceTier, setPerformanceTier]);

  const driving = phase === "driving";

  return (
    <div
      className={`app app--${phase} ${reducedMotion ? "app--reduced-motion" : ""}`}
    >
      <div
        className="scene-shell"
        onPointerDown={(event) => {
          if (
            !driving ||
            (event.target as HTMLElement).closest("button, input, a")
          ) {
            return;
          }
          pointerStart.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => {
          const start = pointerStart.current;
          pointerStart.current = null;
          if (!driving || !start) return;
          const deltaX = event.clientX - start.x;
          const deltaY = event.clientY - start.y;
          if (Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY)) {
            changeLane(
              deltaX < 0 ? -1 : 1,
              event.pointerType === "touch" ? "touch" : "pointer",
            );
          }
        }}
      >
        {canvasFailed ? (
          <StaticTrafficBoard />
        ) : (
          <TrafficCanvas
            billboards={billboardDisplaySnapshots(billboardSlots)}
            billboardsInteractive={billboardsEnabled}
            onBillboardClick={openBillboard}
          />
        )}
        <div className="scene-vignette" aria-hidden="true" />
      </div>

      <TrafficHud />
      <BillboardHud />
      <EntryPanel />
      {phase === "spectating" || phase === "driving" ? (
        <MockTrafficPanel />
      ) : null}

      {driving && laneChangeSequence === 0 ? (
        <p className="lane-hint">
          <span>SWIPE</span> OR USE <kbd>←</kbd> <kbd>→</kbd> TO CHANGE LANES
        </p>
      ) : null}

      {phase === "exiting" ? (
        <div className="exit-banner" role="status">
          <span>NEXT EXIT</span>
          <strong>Returning to the rest of the internet…</strong>
        </div>
      ) : null}

      {phase === "report" ? <TripReport /> : null}
      <GarageModal />
      <InfoModal />
      <BillboardModal />
    </div>
  );
}
